export type WidgetMode = 'admin' | 'liswp_ask';

export const WIDGET_FRAME_NAME = 'co_scientist_widget_frame';
export const WIDGET_ONBOARDING_MODE = 'onboarding';

// Which widget this page is the frame of, or null outside one; analytics tells widget sends apart
export function widgetFrameMode(): 'onboarding' | 'admin' | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  if (params.get('widget') !== 'true') return null;
  return params.get('widget_mode') === WIDGET_ONBOARDING_MODE
    ? 'onboarding'
    : 'admin';
}

export interface WidgetProps {
  mode: WidgetMode;
  userId: number;
  enabled: boolean;
  overlayPath: string;
  settingsPath: string;
  paperPath: string;
  sharePage: boolean;
  paperReference: string | null;
  paperKey: string | null;
  paperTitle: string | null;
  workId: number | null;
  /** The LISWP widget test keeps its chats per paper */
  storageScope: number | null;
}

// Must match widget_storage_key in Demos::CoScientistAgentController
export function widgetStorageKey(
  userId: number,
  storageScope: number | null,
): string {
  return `coscientist-widget:${userId}${storageScope ? `:work:${storageScope}` : ''}`;
}

export interface WidgetContext {
  shared: boolean;
  url: string | null;
  title: string | null;
  text: string | null;
  truncated: boolean;
  paperReference: string | null;
  paperKey: string | null;
  paperPath: string;
}

export function widgetParentOrigin(): string | null {
  try {
    const referrer =
      document.referrer ||
      new URL(window.location.href).searchParams.get('widgetParentOrigin') ||
      '';
    const origin = new URL(referrer).origin;
    const hostname = new URL(origin).hostname;
    const base = window.location.hostname.replace(/^www\./, '');
    return hostname === base || hostname.endsWith(`.${base}`) ? origin : null;
  } catch {
    return null;
  }
}

export function readWidgetState<T>(key: string, fallback: T): T {
  try {
    const value = window.sessionStorage.getItem(key);
    return value === null ? fallback : (JSON.parse(value) as T);
  } catch {
    return fallback;
  }
}

export function writeWidgetState(key: string, value: unknown): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Browsing and chatting still work when storage is unavailable.
  }
}

export function captureWidgetContext(
  props: WidgetProps,
  initialPath: string,
): WidgetContext {
  const shared = props.sharePage && window.location.pathname === initialPath;
  const context: WidgetContext = {
    shared,
    url: null,
    title: null,
    text: null,
    truncated: false,
    paperReference: null,
    paperKey: null,
    paperPath: props.paperPath,
  };
  if (!shared) return context;

  const main =
    document.querySelector('[data-co-scientist-page-context]') ||
    document.querySelector('main, [role="main"], article');
  const snapshot = main?.cloneNode(true) as HTMLElement | undefined;
  snapshot
    ?.querySelectorAll(
      'script, style, noscript, input, textarea, select, button, form, nav, iframe, ' +
        '[contenteditable], [data-private], [hidden], [aria-hidden="true"], ' +
        '.admin-strip, #admin-strip, .co-scientist-widget',
    )
    .forEach((element) => element.remove());
  const text = snapshot?.textContent?.replace(/\s+/g, ' ').trim() ?? '';
  return {
    ...context,
    url: window.location.origin + window.location.pathname,
    title: props.paperTitle || document.title,
    text: text.slice(0, 40_000),
    truncated: text.length > 40_000,
    paperReference: props.paperReference,
    paperKey: props.paperKey,
  };
}

export function attachWidgetFiles(text: string, paths: string[]): string {
  if (!paths.length) return text;
  const files = paths.join('\n');
  return text.includes('<attached_files>')
    ? text.replace('<attached_files>', `<attached_files>\n${files}`)
    : `<attached_files>\n${files}\n</attached_files>\n\n${text}`;
}
